import { useEffect, useState } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Mail, Loader2, Send, ChevronDown, ChevronUp, CheckCircle, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { usePermission } from "@/contexts/RoleAccessContext";
import { createCanHelpers } from "@/utils/rbac";
import {
  getEmailSettings,
  updateEmailSettings,
  setEmailEnabled,
  sendTestEmail,
} from "@/services/mailService";

// Frontend mirror of the backend deriveSmtp — auto-fills the host from the email
// domain so most admins never need to open "Advanced".
function deriveSmtp(email) {
  const domain = String(email || "").split("@")[1]?.toLowerCase() || "";
  if (/(^|\.)gmail\.com$/.test(domain) || domain === "googlemail.com")
    return { host: "smtp.gmail.com", port: 587, secure: false };
  if (/outlook|office365|hotmail|live|microsoft/.test(domain))
    return { host: "smtp.office365.com", port: 587, secure: false };
  if (domain.includes("zoho")) return { host: "smtp.zoho.com", port: 587, secure: false };
  if (domain === "yahoo.com") return { host: "smtp.mail.yahoo.com", port: 587, secure: false };
  return { host: "", port: 587, secure: false };
}

function StatusBadge({ status, enabled }) {
  if (!enabled) return <Badge variant="outline">Off — using shared mailbox</Badge>;
  switch (status) {
    case "active":
      return <Badge>Active</Badge>;
    case "failing":
      return <Badge variant="destructive">Failing — using fallback</Badge>;
    case "inactive":
      return <Badge variant="secondary">Saved — send a test to activate</Badge>;
    default:
      return <Badge variant="outline">Not configured</Badge>;
  }
}

const MailSettingsTab = () => {
  const { can } = usePermission();
  const { canEdit } = createCanHelpers(can);
  const canEditMail = canEdit("settings_company_configuration");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const [enabled, setEnabled] = useState(false);
  const [fromName, setFromName] = useState("");
  const [fromEmail, setFromEmail] = useState("");
  const [appPassword, setAppPassword] = useState("");
  const [hasPassword, setHasPassword] = useState(false);
  const [smtpHost, setSmtpHost] = useState("");
  const [smtpPort, setSmtpPort] = useState(587);
  const [smtpSecure, setSmtpSecure] = useState(false);
  const [status, setStatus] = useState("unconfigured");
  const [lastError, setLastError] = useState(null);
  const [effectiveSender, setEffectiveSender] = useState(null);
  const [testEmail, setTestEmail] = useState("");

  function applySettings(s) {
    if (!s) return;
    setEnabled(!!s.enabled);
    setFromName(s.from_name || "");
    setFromEmail(s.from_email || "");
    setHasPassword(!!s.has_password);
    setSmtpHost(s.smtp_host || "");
    setSmtpPort(s.smtp_port || 587);
    setSmtpSecure(!!s.smtp_secure);
    setStatus(s.status || "unconfigured");
    setLastError(s.last_error || null);
    setEffectiveSender(s.effective_sender || null);
  }

  async function loadSettings() {
    try {
      const s = await getEmailSettings();
      applySettings(s);
    } catch (err) {
      toast.error(err?.response?.data?.error || "Failed to load mail settings");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadSettings();
  }, []);

  function onFromEmailBlur() {
    if (!smtpHost && fromEmail) {
      const d = deriveSmtp(fromEmail);
      if (d.host) {
        setSmtpHost(d.host);
        setSmtpPort(d.port);
        setSmtpSecure(d.secure);
      }
    }
  }

  async function handleToggle(next) {
    setToggling(true);
    setEnabled(next); // optimistic
    try {
      const s = await setEmailEnabled(next);
      applySettings(s);
      toast.success(next ? "Custom mailbox enabled" : "Custom mailbox disabled");
    } catch (err) {
      setEnabled(!next); // revert
      toast.error(err?.response?.data?.error || "Failed to update toggle");
    } finally {
      setToggling(false);
    }
  }

  async function handleSave() {
    if (!fromName || !fromEmail) {
      toast.error("From Name and From Email are required");
      return;
    }
    if (!appPassword && !hasPassword) {
      toast.error("App Password is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        enabled,
        from_name: fromName,
        from_email: fromEmail,
        smtp_host: smtpHost,
        smtp_port: Number(smtpPort) || 587,
        smtp_secure: smtpSecure,
      };
      if (appPassword) payload.app_password = appPassword;
      const s = await updateEmailSettings(payload);
      setAppPassword("");
      applySettings(s);
      toast.success("Email settings saved");
    } catch (err) {
      toast.error(err?.response?.data?.error || "Failed to save email settings");
    } finally {
      setSaving(false);
    }
  }

  async function handleTest() {
    if (!testEmail) {
      toast.error("Enter a test recipient email");
      return;
    }
    setTesting(true);
    try {
      const s = await sendTestEmail(testEmail);
      if (s) applySettings(s);
      else await loadSettings();
      toast.success(`Test email sent to ${testEmail}`);
    } catch (err) {
      toast.error(err?.response?.data?.error || "Test send failed");
      await loadSettings(); // refresh badge/last-error after a failed test
    } finally {
      setTesting(false);
    }
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading mail settings…
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center space-x-2">
            <Mail className="h-5 w-5" />
            <span>Mail Settings</span>
          </CardTitle>
          <StatusBadge status={status} enabled={enabled} />
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Currently sending as */}
        {effectiveSender ? (
          <div className="rounded-lg border border-primary/30 bg-primary/10 p-3 dark:bg-primary/5">
            <div className="flex items-start gap-2.5">
              <CheckCircle className="mt-0.5 size-4 shrink-0 text-primary" />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-primary">Currently sending as :</p>
                <p className="text-sm">
                  {effectiveSender.name
                    ? `${effectiveSender.name} <${effectiveSender.email}>`
                    : effectiveSender.email}
                  {effectiveSender.source === "company"
                    ? " (your mailbox)"
                    : " (shared Zenith mailbox)"}
                </p>
              </div>
            </div>
          </div>
        ) : null}

        {/* Last error */}
        {lastError && status === "failing" ? (
          <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-3 dark:bg-destructive/5">
            <div className="flex items-start gap-2.5">
              <AlertCircle className="mt-0.5 size-4 shrink-0 text-destructive" />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-destructive">Last send failed</p>
                <p className="text-sm text-foreground break-words">{lastError}</p>
              </div>
            </div>
          </div>
        ) : null}

        {/* Master switch */}
        <div className="flex items-center justify-between">
          <div>
            <Label>Use my company mailbox for outgoing emails</Label>
            <p className="text-xs text-muted-foreground">
              When off, emails are sent from the shared Zenith mailbox.
            </p>
          </div>
          <Switch
            checked={enabled}
            disabled={!canEditMail || toggling}
            onCheckedChange={handleToggle}
          />
        </div>

        {/* Config form */}
        {enabled && (
          <>
            <Separator />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>From Name</Label>
                <Input
                  disabled={!canEditMail}
                  placeholder="e.g., Sunshine HR"
                  value={fromName}
                  onChange={(e) => setFromName(e.target.value)}
                />
              </div>
              <div>
                <Label>From Email</Label>
                <Input
                  disabled={!canEditMail}
                  placeholder="e.g., hr@sunshine.com"
                  value={fromEmail}
                  onBlur={onFromEmailBlur}
                  onChange={(e) => setFromEmail(e.target.value)}
                />
              </div>
              <div>
                <Label>App Password</Label>
                <Input
                  type="password"
                  disabled={!canEditMail}
                  placeholder={
                    hasPassword
                      ? "•••••••• (saved — leave blank to keep)"
                      : "Mailbox app password"
                  }
                  value={appPassword}
                  onChange={(e) => setAppPassword(e.target.value)}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Use an app-specific password (Gmail/365), not your login password.
                </p>
              </div>
            </div>

            {/* Advanced */}
            <div>
              <button
                type="button"
                className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
                onClick={() => setShowAdvanced((v) => !v)}
              >
                {showAdvanced ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
                Advanced (SMTP)
              </button>

              {showAdvanced && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                  <div>
                    <Label>SMTP Host</Label>
                    <Input
                      disabled={!canEditMail}
                      placeholder="smtp.gmail.com"
                      value={smtpHost}
                      onChange={(e) => setSmtpHost(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>SMTP Port</Label>
                    <Input
                      type="number"
                      disabled={!canEditMail}
                      placeholder="587"
                      value={smtpPort}
                      onChange={(e) => setSmtpPort(e.target.value)}
                    />
                  </div>
                  <div className="flex items-center justify-between md:col-span-2">
                    <Label>Use SSL (port 465)</Label>
                    <Switch
                      checked={smtpSecure}
                      disabled={!canEditMail}
                      onCheckedChange={setSmtpSecure}
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end">
              <Button onClick={handleSave} disabled={!canEditMail || saving}>
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Save changes
              </Button>
            </div>

            <Separator />

            {/* Send test */}
            <div>
              <Label>Send a test email</Label>
              <div className="flex gap-2 max-w-md mt-1">
                <Input
                  type="email"
                  placeholder="you@example.com"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                />
                <Button
                  variant="outline"
                  onClick={handleTest}
                  disabled={!hasPassword || testing}
                  title={!hasPassword ? "Save your settings first" : undefined}
                >
                  {testing ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4 mr-2" />
                  )}
                  Send test
                </Button>
              </div>
              {!hasPassword && (
                <p className="text-xs text-muted-foreground mt-1">
                  Save your settings before sending a test.
                </p>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default MailSettingsTab;
